import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { RefreshCw } from 'lucide-react'
import threeDotsIcon from '../../../../assets/three-dots-icon.svg'
import { STATUS_LABELS } from './constants'
import { PurchaseOrder, PurchaseOrderStatus } from './types'
import ReceiveOrderConfirmation from './ReceiveOrderConfirmation'
import { StatusBadge } from './style'
import {
  MenuItem,
  MenuLayer,
  MenuPanel,
  MenuTrigger,
  MenuTriggerIcon,
  SubMenu,
  SubMenuItem,
} from './actionsMenuStyle'

const MAIN_MENU_WIDTH = 200
const SUB_MENU_WIDTH = 190
const MENU_GAP = 4
const MENU_HEIGHT = 60
const SUB_MENU_HEIGHT = 190

const STATUS_OPTIONS = Object.keys(STATUS_LABELS) as PurchaseOrderStatus[]

interface StatusActionsMenuProps {
  order: PurchaseOrder
  onStatusChange: (orderId: number, status: PurchaseOrderStatus) => Promise<void>
}

const StatusActionsMenu: React.FC<StatusActionsMenuProps> = ({ order, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [pendingReceipt, setPendingReceipt] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // R8: uma OC recebida já deu entrada em estoque e não pode mais mudar de status.
  const isLocked = order.status === 'RECEBIDO'

  const updatePosition = (submenuOpen: boolean) => {
    if (!buttonRef.current) return

    const rect = buttonRef.current.getBoundingClientRect()
    const totalWidth = submenuOpen ? MAIN_MENU_WIDTH + SUB_MENU_WIDTH + MENU_GAP : MAIN_MENU_WIDTH
    const menuHeight = submenuOpen ? SUB_MENU_HEIGHT : MENU_HEIGHT
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpwards = spaceBelow < menuHeight && rect.top > menuHeight

    setPosition({
      top: openUpwards ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - totalWidth),
    })
  }

  useEffect(() => {
    if (!isOpen) return
    updatePosition(showStatusSubmenu)
  }, [isOpen, showStatusSubmenu])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setIsOpen(false)
      setShowStatusSubmenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const closeMenu = () => {
    setIsOpen(false)
    setShowStatusSubmenu(false)
  }

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!isOpen) updatePosition(false)
    setIsOpen((open) => !open)
    if (isOpen) setShowStatusSubmenu(false)
  }

  const runStatusChange = async (status: PurchaseOrderStatus) => {
    setIsLoading(true)
    try {
      await onStatusChange(order.id, status)
      closeMenu()
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectStatus = (status: PurchaseOrderStatus) => {
    if (isLoading || status === order.status) return
    // Receber exige confirmação: a entrada em estoque é irreversível.
    if (status === 'RECEBIDO') {
      setPendingReceipt(true)
      return
    }
    runStatusChange(status)
  }

  return (
    <>
      <MenuTrigger ref={buttonRef} type='button' onClick={handleToggle} aria-label='Ações'>
        <MenuTriggerIcon src={threeDotsIcon} alt='Ações' />
      </MenuTrigger>

      {isOpen &&
        ReactDOM.createPortal(
          <MenuLayer ref={menuRef} $top={position.top} $left={position.left}>
            {showStatusSubmenu && (
              <SubMenu>
                {STATUS_OPTIONS.map((status) => (
                  <SubMenuItem
                    key={status}
                    $disabled={status === order.status}
                    onClick={() => handleSelectStatus(status)}
                  >
                    <StatusBadge $status={status}>{STATUS_LABELS[status]}</StatusBadge>
                  </SubMenuItem>
                ))}
              </SubMenu>
            )}

            <MenuPanel>
              <MenuItem
                $active={showStatusSubmenu}
                $disabled={isLocked}
                title={
                  isLocked
                    ? 'Esta ordem de compra já foi recebida e não pode ter o status alterado.'
                    : undefined
                }
                onClick={() => !isLocked && setShowStatusSubmenu((show) => !show)}
              >
                <RefreshCw size={16} />
                Alterar status
              </MenuItem>
            </MenuPanel>
          </MenuLayer>,
          document.body,
        )}

      <ReceiveOrderConfirmation
        isOpen={pendingReceipt}
        code={order.code}
        title={order.title}
        onCancel={() => setPendingReceipt(false)}
        onConfirm={() => {
          setPendingReceipt(false)
          runStatusChange('RECEBIDO')
        }}
      />
    </>
  )
}

export default StatusActionsMenu
